import { useEffect } from 'react';

interface PageMeta {
  title: string;
  description?: string;
}

/**
 * Sets <title> and the <meta name="description"> on mount and any time the
 * arguments change. Restores the previous values on unmount so unrelated
 * navigations don't leak page state.
 *
 * Per-page meta belongs in the page component itself; the static index.html
 * carries the homepage defaults so social-card crawlers (which do not execute
 * JavaScript) still get the right OG title and description.
 */
export function usePageMeta(meta: PageMeta): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = meta.title;

    let descriptionEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    let previousDescription: string | null = null;
    if (meta.description) {
      if (!descriptionEl) {
        descriptionEl = document.createElement('meta');
        descriptionEl.name = 'description';
        document.head.appendChild(descriptionEl);
      }
      previousDescription = descriptionEl.getAttribute('content');
      descriptionEl.setAttribute('content', meta.description);
    }

    return () => {
      document.title = previousTitle;
      if (descriptionEl && previousDescription !== null) {
        descriptionEl.setAttribute('content', previousDescription);
      }
    };
  }, [meta.title, meta.description]);
}
